import React, { useState } from 'react';
import { SlidersHorizontal, X, Trash2, Filter } from 'lucide-react';

interface FilterOption {
  key: string;
  label: string;
  type?: 'select' | 'text';
  options?: { value: string; label: string }[] | string[];
}

interface ActiveFilter {
  key: string;
  label: string;
  type?: 'select' | 'text';
  value: string;
  options: { value: string; label: string }[];
}

interface ReportFilterBarProps {
  availableFilters: FilterOption[];
  showDateRange?: boolean;
  onApply?: (filters: Record<string, string>, startDate?: string, endDate?: string) => void;
}

function normalizeOptions(opts: { value: string; label: string }[] | string[]): { value: string; label: string }[] {
  if (!opts || opts.length === 0) return [];
  if (typeof opts[0] === 'string') return (opts as string[]).map((o) => ({ value: o, label: o }));
  return opts as { value: string; label: string }[];
}

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({ availableFilters, showDateRange = true, onApply }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<ActiveFilter[]>([]);
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [draftFilters, setDraftFilters] = useState<ActiveFilter[]>([]);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const hasActiveFilters = appliedFilters.length > 0 || appliedStartDate || appliedEndDate;

  const openModal = () => {
    setDraftFilters([...appliedFilters]);
    setDraftStartDate(appliedStartDate);
    setDraftEndDate(appliedEndDate);
    setIsModalOpen(true);
    setShowAddMenu(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setShowAddMenu(false);
  };

  const addFilter = (filter: FilterOption) => {
    const exists = draftFilters.find((f) => f.key === filter.key);
    if (!exists) {
      setDraftFilters([...draftFilters, { key: filter.key, label: filter.label, type: filter.type || 'select', value: '', options: normalizeOptions(filter.options || []) }]);
    }
    setShowAddMenu(false);
  };

  const removeFilter = (key: string) => {
    setDraftFilters(draftFilters.filter((f) => f.key !== key));
  };

  const updateFilterValue = (key: string, value: string) => {
    setDraftFilters(draftFilters.map((f) => (f.key === key ? { ...f, value } : f)));
  };

  const applyFilters = () => {
    const applied = draftFilters.filter((f) => f.value);
    setAppliedFilters(applied);
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    setIsModalOpen(false);
    setShowAddMenu(false);
    if (onApply) {
      const filterMap: Record<string, string> = {};
      applied.forEach((f) => { filterMap[f.key] = f.value; });
      onApply(filterMap, draftStartDate || undefined, draftEndDate || undefined);
    }
  };

  const clearFilters = () => {
    setAppliedFilters([]);
    setDraftFilters([]);
    setDraftStartDate('');
    setDraftEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    if (onApply) onApply({});
  };

  const availableToAdd = availableFilters.filter((af) => !draftFilters.find((df) => df.key === af.key));

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={openModal}
          className={`flex items-center gap-2 px-3 py-1.5 text-[13px] border rounded transition-colors ${
            hasActiveFilters
              ? 'border-primary bg-primary/5 text-primary font-medium'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filter
        </button>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-[13px] text-gray-500 hover:text-gray-700 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white rounded-lg shadow-xl w-[520px] max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <h3 className="text-[18px] font-semibold text-primary">Filters</h3>
              <button
                onClick={closeModal}
                className="w-7 h-7 flex items-center justify-center rounded bg-primary text-white hover:bg-primary/80 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 pb-2 space-y-5 min-h-[150px] ">
              {showDateRange && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[14px] font-medium text-gray-700">Start Date</label>
                      <button onClick={() => setDraftStartDate('')} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="date"
                      value={draftStartDate}
                      onChange={(e) => setDraftStartDate(e.target.value)}
                      className="w-full h-[42px] px-3 text-[14px] border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[14px] font-medium text-gray-700">End Date</label>
                      <button onClick={() => setDraftEndDate('')} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="date"
                      value={draftEndDate}
                      onChange={(e) => setDraftEndDate(e.target.value)}
                      className="w-full h-[42px] px-3 text-[14px] border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                    />
                  </div>
                </>
              )}

              {draftFilters.map((filter) => (
                <div key={filter.key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[14px] font-medium text-gray-700">{filter.label}</label>
                    <button onClick={() => removeFilter(filter.key)} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {filter.type === 'text' ? (
                    <input
                      type="text"
                      value={filter.value}
                      onChange={(e) => updateFilterValue(filter.key, e.target.value)}
                      placeholder={`Search ${filter.label}...`}
                      className="w-full h-[42px] px-3 text-[14px] border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                    />
                  ) : (
                    <select
                      value={filter.value}
                      onChange={(e) => updateFilterValue(filter.key, e.target.value)}
                      className="w-full h-[42px] px-3 text-[14px] border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                    >
                      <option value="">Select...</option>
                      {filter.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between px-6 py-4 mt-2">
              <div className="relative">
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-secondary rounded hover:bg-secondary/80 transition-colors"
                  disabled={availableToAdd.length === 0}
                >
                  <Filter className="w-3.5 h-3.5" />
                  Add Filter
                </button>
                {showAddMenu && availableToAdd.length > 0 && (
                  <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[200px] z-10">
                    {availableToAdd.map((filter) => (
                      <button
                        key={filter.key}
                        onClick={() => addFilter(filter)}
                        className="w-full text-left px-4 py-2 text-[14px] text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-1.5 text-[13px] font-medium text-white bg-primary rounded hover:bg-primary/80 transition-colors"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportFilterBar;
